import {
    addSalesforceToSubscription,
    getSalesforceIntegration,
    getSalesforceSchemaObjects,
    getSalesforceSchemaObjectDetails,
    saveSalesforceJoinKeys,
    deleteSalesforceIntegration,
} from './salesforce.utils';
import Vue from 'vue';

export const SALESFORCE_AUTHENTICATION = {
    ERROR: -1,
    NOT_STARTED: 0,
    PENDING: 1,
    COMPLETE: 2,
};

const salesforceDefaultState = {
    created: false,
    auth: {
        status: SALESFORCE_AUTHENTICATION.NOT_STARTED,
        error: null,
        info: {},
    },
    lookupError: null,
    schema: {
        error: null,
        map: {},
    },
    schemasById: {
        error: null,
        map: {},
    },
    tables: {
        error: null,
        list: [],
    },
    keys: {
        contact: {},
        account: {},
        parentAccount: {},
    },
    reports: {},
    syncInfo: [
        {
            kind: 'contact',
            lastRunAt: 0,
            lastRunStatus: 'NOT_RUN',
            lastRunStatusMessage: '',
            lastSyncStartedAt: 0,
        },
        {
            kind: 'account',
            lastRunAt: 0,
            lastRunStatus: 'NOT_RUN',
            lastRunStatusMessage: '',
            lastSyncStartedAt: 0,
        },
        {
            kind: 'parentAccount',
            lastRunAt: 0,
            lastRunStatus: 'NOT_RUN',
            lastRunStatusMessage: '',
            lastSyncStartedAt: 0,
        },
    ],
    isSandbox: false,
};

const salesforce = {
    namespaced: true,
    state: salesforceDefaultState,
    mutations: {
        setSalesforceCreated (state, { value = true }) {
            Vue.set(state, 'created', value);
        },
        setOAuthInfoForSalesforce (state, { info }) {
            Vue.set(state.auth, 'info', info);
        },
        setSalesforceAuthStatus (state, { status, authorizedBy = null, authorizedAt = null }) {
            Vue.set(state.auth, 'status', status);
            Vue.set(state.auth, 'authorizedBy', authorizedBy);
            Vue.set(state.auth, 'authorizedAt', authorizedAt);
        },
        setAuthenticationError (state, { error }) {
            Vue.set(state.auth, 'error', error);
        },
        setSalesforceLookupError (state, { error }) {
            Vue.set(state, 'lookupError', error);
        },
        setSalesforceSchemaError (state, { error }) {
            Vue.set(state.schema, 'error', error);
            Vue.set(state.schemasById, 'error', error);
        },
        clearSalesforceSchemaError (state) {
            Vue.set(state.schema, 'error', null);
            Vue.set(state.schemasById, 'error', null);
        },
        setSalesforceTablesError (state, { error }) {
            Vue.set(state.tables, 'error', error);
        },
        clearSalesforceTablesError (state) {
            Vue.set(state.tables, 'error', null);
        },
        setSalesforceSchemaObject (state, { key, object }) {
            const mergeObject = Object.assign(state.schema.map[key] || {}, object);
            Vue.set(state.schema.map, key, mergeObject);
        },
        setSalesforceSchemaById (state, { key, object }) {
            Vue.set(state.schemasById.map, key, { ...object });
        },
        setSalesforceTables (state, { tables }) {
            Vue.set(state.tables, 'list', [...tables]);
        },
        setSalesforceJoinKeys (state, { keys }) {
            const joinKeys = { ...state.keys, ...keys };
            Vue.set(state, 'keys', joinKeys);
        },
        setSalesforceSyncInfo (state, { syncInfo }) {
            const info = state.syncInfo;
            syncInfo.forEach((level) => {
                info[info.findIndex((elem) => elem.kind === level.kind)] = level;
            });
            Vue.set(state, 'syncInfo', info);
        },
        setSalesforceReports (state, { reports }) {
            if (reports) Vue.set(state, 'reports', reports);
        },
        setSalesforceDefaultState (state) {
            Vue.set(state, salesforceDefaultState);
        },
        setSalesforceIsSandbox (state, { isSandbox }) {
            state.isSandbox = isSandbox;
        },
    },
    actions: {
        async addSalesforceToSubscription ({ commit }, { kind }) {
            await addSalesforceToSubscription(kind);

            commit('setSalesforceCreated', { value: true });
            commit('setSalesforceLookupError', { error: null });
        },
        async fetchSalesforceInformation ({ commit }) {
            try {
                const { data } = await getSalesforceIntegration();

                if (!Object.keys(data)) {
                    commit('setCreated', { value: false });

                    return;
                }

                commit('setSalesforceCreated', { value: true });

                const authStatus = {
                    ERROR: SALESFORCE_AUTHENTICATION.ERROR,
                    OK: SALESFORCE_AUTHENTICATION.COMPLETE,
                    PENDING: SALESFORCE_AUTHENTICATION.PENDING,
                };

                commit('setSalesforceAuthStatus', {
                    status: authStatus[data.authStatus] || SALESFORCE_AUTHENTICATION.NOT_STARTED,
                    authorizedAt: data.authorizedAt,
                    authorizedBy: data.authorizedBy,
                });

                if (data.syncInfo) commit('setSalesforceSyncInfo', { syncInfo: data.syncInfo });
                if (data.data.reports) commit('setSalesforceReports', { reports: data.data.reports });
                if (data.data.joinKeys) commit('setSalesforceJoinKeys', { keys: data.data.joinKeys });
                if (data.customUri) commit('setSalesforceIsSandbox', { isSandbox: true });
            } catch (error) {
                commit('setSalesforceLookupError', { error });
            }
        },
        async loadSalesforceSchemaObjects ({ commit }, { omitSchemaObjects = false } = {}) {
            try {
                const { data } = await getSalesforceSchemaObjects();

                if (!data.length) {
                    return;
                }

                if (!omitSchemaObjects) {
                    data.forEach((object) => {
                        commit('setSalesforceSchemaObject', {
                            key: object.name,
                            object: { label: object.label, name: object.name },
                        });
                    });
                }

                commit('setSalesforceTables', {
                    tables: data,
                });
                commit('clearSalesforceSchemaError');
                commit('clearSalesforceTablesError');
            } catch (error) {
                commit('setSalesforceSchemaError', { error });
                commit('setSalesforceTablesError', { error });
            }
        },
        async loadSalesforceSchemaObjectDetails ({ commit }, { objectKey }) {
            try {
                const { data } = await getSalesforceSchemaObjectDetails(objectKey);

                commit('setSalesforceSchemaObject', {
                    key: objectKey,
                    object: data,
                });

                commit('setSalesforceSchemaById', {
                    key: objectKey,
                    object: data,
                });

                commit('clearSalesforceSchemaError');
            } catch (error) {
                commit('setSalesforceSchemaError', { error });
            }
        },
        async saveSalesforceJoinKeys ({ commit }, { keys, kind }) {
            try {
                await saveSalesforceJoinKeys({ joinKeys: keys[kind].keys, reports: keys[kind].reports });
                commit('setSalesforceJoinKeys', { keys: keys[kind].keys });
            } catch (error) {
                commit('setSalesforceSchemaError', { error });
            }
        },
        async saveAllSalesforceJoinKeys ({ commit }, { keys }) {
            try {
                await saveSalesforceJoinKeys({ joinKeys: keys });
                commit('setSalesforceJoinKeys', { keys });
            } catch (error) {
                commit('setSalesforceSchemaError', { error });
            }
        },
        async deleteIntegration ({ commit }) {
            try {
                await deleteSalesforceIntegration();
                commit('setSalesforceDefaultState');
            } catch (error) {
                throw error;
            }
        },
    },
    getters: {
        getSalesforceJoinKeys: (state) => () => state.keys,
        getSalesforceSchemaById: (state) => (id) => state.schemasById.map[id] || {},
        getSalesforceTables: (state) => () => state.tables.list,
    },
};

export default salesforce;
